import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResult } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { MailingList } from './types/mailing-list.type';

/**
 * Updates an existing mailing list using the provided Ky HTTP client instance.
 *
 * @param instance - The KyInstance used to make the HTTP request.
 * @param mailingList - The MailingList object containing updated data. Must include a valid `id`.
 * @returns A promise resolving to an OperationResult indicating the outcome of the update operation.
 * @throws Will throw an error if the mailing list `id` is not defined or if the HTTP request fails.
 */
export const updateMailingList = async (instance: KyInstance, mailingList: MailingList) => {
  try {
    if (isNotDefined(mailingList.id)) {
      throw new Error('Mailing list id is required for update.');
    }

    return await instance.put<OperationResult>(formatString(Uri.mailingList.update, mailingList.id.toString()), {
      json: mailingList,
    });
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
